import React, { createContext, useContext, useMemo } from 'react'
import type { ToolEffectContext } from './types'

/**
 * React Context for providing tool effect controllers to the component tree.
 * This allows tool effect handlers to access map controls, gallery controls, etc.
 */
const ToolEffectContextInstance = createContext<ToolEffectContext | undefined>(
  undefined
)

export interface ToolEffectProviderProps {
  /**
   * The context value containing controllers and services
   */
  value: ToolEffectContext

  /**
   * Child components that will have access to the tool effect context
   */
  children: React.ReactNode
}

/**
 * Provider component that makes tool effect context available to child components.
 * Typically used at a high level in the component tree (e.g., TripPage) to provide
 * controllers like mapControls to chat components that trigger effects.
 *
 * @example
 * ```tsx
 * function TripPage() {
 *   const mapControls = useMapControlsOptional()
 *
 *   return (
 *     <ToolEffectProvider value={{ mapControls }}>
 *       <ChatPanel />
 *     </ToolEffectProvider>
 *   )
 * }
 * ```
 */
export function ToolEffectProvider({
  value,
  children,
}: ToolEffectProviderProps) {
  // Memoize the context value to prevent unnecessary re-renders
  const contextValue = useMemo(() => value, [value])

  return (
    <ToolEffectContextInstance.Provider value={contextValue}>
      {children}
    </ToolEffectContextInstance.Provider>
  )
}

/**
 * Hook to access the tool effect context.
 * Returns the context value containing controllers and services.
 * May return undefined if used outside of a ToolEffectProvider.
 *
 * @example
 * ```tsx
 * function ChatMessage() {
 *   const effectContext = useToolEffectContext()
 *
 *   useEffect(() => {
 *     if (toolCompleted) {
 *       executeToolEffects(toolName, toolResponse, effectContext)
 *     }
 *   }, [toolCompleted])
 * }
 * ```
 */
export function useToolEffectContext(): ToolEffectContext {
  const context = useContext(ToolEffectContextInstance)

  // Return empty context if provider is not present
  // This allows components to work even when provider is missing
  return context ?? {}
}
